import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Button, TextField, Radio, Checkbox,
  FormControlLabel, RadioGroup, FormGroup, Alert, CircularProgress, Box, LinearProgress, Stack,
} from '@mui/material';
import QuizLayout from './QuizLayout';
import { quizService } from './quizService';
import {
  toggleSelection, isQuestionAnswered, buildAttemptPayload, TAKER_NAME_KEY,
} from './quizLogic';

export default function TakeQuiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState(localStorage.getItem(TAKER_NAME_KEY) || '');
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // {questionId: [optionIds]}
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    quizService.getQuiz(quizId).then(setQuiz).catch((e) => setError(e.message));
  }, [quizId]);

  if (error) return <QuizLayout title="Quiz"><Alert severity="error">{error}</Alert></QuizLayout>;
  if (!quiz) return <QuizLayout title="Quiz"><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></QuizLayout>;

  // ---- empty-quiz guard ----
  if (quiz.questions.length === 0) {
    return (
      <QuizLayout title={quiz.title}>
        <Alert severity="info" sx={{ mb: 2 }}>This quiz has no questions yet. Check back later.</Alert>
        <Button variant="contained" onClick={() => navigate('/quizzes')} data-testid="quiz-empty-back">
          Back to quizzes
        </Button>
      </QuizLayout>
    );
  }

  // ---- name gate ----
  if (!started) {
    return (
      <QuizLayout title={quiz.title}>
        <Card><CardContent>
          <Typography sx={{ mb: 2 }}>Enter your name to begin. Your results are recorded under this name.</Typography>
          <TextField
            fullWidth label="Your name" value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ 'data-testid': 'taker-name-input' }} sx={{ mb: 2 }}
          />
          <Button
            variant="contained" disabled={!name.trim()} data-testid="start-quiz"
            onClick={() => { localStorage.setItem(TAKER_NAME_KEY, name.trim()); setStarted(true); }}
          >Start quiz</Button>
        </CardContent></Card>
      </QuizLayout>
    );
  }

  const question = quiz.questions[index];
  const selected = answers[question.id] || [];
  const isLast = index === quiz.questions.length - 1;

  const pick = (optionId) => {
    setAnswers((prev) => ({ ...prev, [question.id]: toggleSelection(question, prev[question.id] || [], optionId) }));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = buildAttemptPayload(name.trim(), answers);
      const result = await quizService.submitAttempt(quizId, payload);
      navigate(`/quizzes/${quizId}/result/${result.attempt_id}`, { state: { result } });
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  const multi = question.type === 'multi';
  const progress = ((index + 1) / quiz.questions.length) * 100;

  return (
    <QuizLayout title={quiz.title}>
      <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
      <Typography variant="caption" color="text.secondary">Question {index + 1} of {quiz.questions.length}</Typography>
      <Card sx={{ mt: 1 }}><CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>{question.prompt}</Typography>
        {multi && <Typography variant="caption" color="text.secondary">Select all that apply</Typography>}
        {multi ? (
          <FormGroup>
            {question.options.map((o) => (
              <FormControlLabel key={o.id}
                control={<Checkbox checked={selected.includes(o.id)} onChange={() => pick(o.id)} />}
                label={o.text} />
            ))}
          </FormGroup>
        ) : (
          <RadioGroup value={selected[0] ?? ''}>
            {question.options.map((o) => (
              <FormControlLabel key={o.id} value={o.id}
                control={<Radio checked={selected.includes(o.id)} onChange={() => pick(o.id)} />}
                label={o.text} />
            ))}
          </RadioGroup>
        )}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>Back</Button>
          <Box sx={{ flexGrow: 1 }} />
          {!isLast && (
            <Button variant="contained" data-testid="quiz-next"
              disabled={!isQuestionAnswered(question, selected)}
              onClick={() => setIndex((i) => i + 1)}>Next</Button>
          )}
          {isLast && (
            <Button variant="contained" color="success" data-testid="quiz-submit"
              disabled={!isQuestionAnswered(question, selected) || submitting}
              onClick={submit}>Submit</Button>
          )}
        </Stack>
      </CardContent></Card>
    </QuizLayout>
  );
}
